"use client";

import Link from "next/link";
import cls from "./page.module.css";
import { Article } from "@/data/mockDataArticles";
import { getBlogPagePath } from "@/utils/consts";
import { BlogPostHero } from "@/components/BlogPage/BlogPostHero/BlogPostHero";
import { BlogAuthor } from "@/components/BlogPage/BlogAuthor/BlogAuthor";
import { BlogPromoCard } from "@/components/BlogPage/BlogPromoCard/BlogPromoCard";
import { BlogTableOfContents, TocItem } from "@/components/BlogPage/BlogTableOfContents/BlogTableOfContents";
import { BlogFaq } from "@/components/BlogPage/BlogFaq/BlogFaq";
import { BlogContent } from "@/components/BlogPage/BlogContent/BlogContent";
import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_AUTHOR = {
    name: "Dmytro Serebrych",
    role: "CEO & Lead of Production",
    avatar: "/team/teammate7.jpg",
    href: "https://www.linkedin.com/in/dmytro-serebrych-01080319a/",
};

const FAQ_ID = "faq";

/** Distance from the viewport top (fixed header + breathing room) that marks the "current" heading. */
const HEADING_OFFSET = 140;

/** Article titles carry an SEO suffix ("… | UData Blog") that should not show up on the page. */
const stripTitleSuffix = (title: string) => title.replace(/\s*\|\s*UData(\s+Blog)?\s*$/i, "");

const buildToc = (article: Article | null, hasFaq: boolean): TocItem[] => {
    if (!article) return [];

    const items: TocItem[] = article.blocks.flatMap((block) =>
        block.type === "heading" && block.level === 2 && block.id
            ? [{ id: block.id, text: block.tocLabel ?? block.text }]
            : []
    );

    if (items.length > 0 && hasFaq) {
        items.push({ id: FAQ_ID, text: "FAQ" });
    }

    return items;
};

interface Props {
    article: Article | null;
    parentLabel?: string;
    parentHref?: string;
}

export function BlogPostClient({
    article,
    parentLabel = "Blog",
    parentHref = getBlogPagePath(),
}: Props) {
    const articleRef = useRef<HTMLElement>(null);
    const faq = article?.faq ?? [];

    // Headings come straight from the blocks, so the table of contents is there on the first paint.
    const tocItems = useMemo(() => buildToc(article, faq.length > 0), [article, faq.length]);

    const [activeId, setActiveId] = useState<string>("");
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (tocItems.length === 0) return;

        let frame = 0;

        const update = () => {
            frame = 0;

            const elements = tocItems
                .map(({ id }) => document.getElementById(id))
                .filter((element): element is HTMLElement => element !== null);

            if (elements.length === 0) return;

            const scrollBottom = window.scrollY + window.innerHeight;
            const atPageBottom = scrollBottom >= document.body.scrollHeight - 2;

            // The active heading is the last one that has crossed the line just below the header.
            let current = elements[0];
            for (const element of elements) {
                if (element.getBoundingClientRect().top <= HEADING_OFFSET) {
                    current = element;
                } else {
                    break;
                }
            }
            setActiveId(atPageBottom ? elements[elements.length - 1].id : current.id);

            const articleElement = articleRef.current;
            if (articleElement) {
                const { top, height } = articleElement.getBoundingClientRect();
                const scrolled = HEADING_OFFSET - top;
                const readable = height - (window.innerHeight - HEADING_OFFSET);
                const ratio = readable > 0 ? scrolled / readable : 1;
                setProgress(Math.min(100, Math.max(0, ratio * 100)));
            }
        };

        const handleScroll = () => {
            if (frame) return;
            frame = window.requestAnimationFrame(update);
        };

        update();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);

        return () => {
            if (frame) window.cancelAnimationFrame(frame);
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [tocItems]);

    if (!article) {
        return (
            <main className={cls.main}>
                <div className={"spacer"} />
                <div className={cls.notFound}>
                    <h1>Page not found</h1>
                    <Link href={parentHref}>← Back to {parentLabel}</Link>
                </div>
            </main>
        );
    }

    const author = article.author ?? DEFAULT_AUTHOR;

    // Below 768px only the table of contents moves into the article — after the CTA, before the first h2.
    const mobileSidebar = (
        <div className={cls.mobileSidebar}>
            <BlogTableOfContents items={tocItems} activeId={activeId} collapsible />
        </div>
    );

    return (
        <main className={cls.main}>
            <div className={"spacer"} />

            <BlogPostHero
                title={article.headline ?? stripTitleSuffix(article.title)}
                breadcrumbTitle={article.breadcrumbTitle}
                date={article.updatedAt ?? article.date}
                dateLabel={article.updatedAt ? "Updated" : article.dateLabel}
                readTime={article.readTime}
                reviewedBy={article?.reviewedBy?.name}
                reviewedByJobTitle={article?.reviewedBy?.jobTitle}
                parentLabel={parentLabel}
                parentHref={parentHref}
            />

            <div className={cls.pageContainer}>
                <div className={cls.pageWrapper}>
                    <aside className={cls.sidebar}>
                        <BlogPromoCard className={cls.promoCard} />

                        <div className={cls.tocSticky}>
                            <BlogTableOfContents items={tocItems} activeId={activeId} progress={progress} />
                        </div>
                    </aside>

                    <article ref={articleRef} className={cls.article}>
                        <BlogAuthor
                            name={author.name}
                            role={author.role}
                            avatar={author.avatar}
                            href={author.href}
                        />

                        <BlogContent blocks={article.blocks} slot={mobileSidebar} />

                        {faq.length > 0 && <BlogFaq items={faq} title={article.faqTitle} id={FAQ_ID} />}

                        {article.afterFaqBlocks?.length ? (
                            <BlogContent blocks={article.afterFaqBlocks} />
                        ) : null}
                    </article>
                </div>
            </div>
        </main>
    );
}
